<template>
  <form class="space-y-2" @submit.prevent="() => {}">
    <template
      v-if="
        (!mode || mode === 'single') &&
        (!singleIntegrationId || typeof singleIntegrationId === 'string')
      "
    >
      <!-- @vue-ignore -->
      <IntegrationPickerOption
        v-for="integration in userIntegrations"
        :key="integration.id"
        v-model="singleIntegrationId"
        :user-integration="integration"
        :platform="platform"
        @update:model-value="onUpdate"
      />
    </template>
    <template v-if="mode === 'multiple'">
      <IntegrationPickerCheckboxOption
        v-for="integration in userIntegrations"
        :key="integration.id"
        v-model="selectedOptions[integration.id]"
        :user-integration="integration"
        :platform="platform"
        @update:model-value="onUpdate"
      />
    </template>
  </form>
</template>

<script
  setup
  lang="ts"
  generic="Mode extends 'single' | 'multiple' = 'single' | 'multiple'"
>
import type { IntegrationTypes, IUserIntegration } from "@/types/account";
import IntegrationPickerOption from "./IntegrationPickerRadioOption.vue";
import IntegrationPickerCheckboxOption from "./IntegrationPickerCheckboxOption.vue";

const props = defineProps<{
  mode: Mode;
  userIntegrations: IUserIntegration<IntegrationTypes>[];
  platform: IntegrationTypes;
  modelValue: Mode extends "single" ? string : string[];
}>();

const emit = defineEmits<{
  (e: "update:modelValue", value: Mode extends "single" ? string : string[]): void;
}>();

// Create separate refs for different modes to avoid complex generic types
const singleIntegrationId = ref<string>(
  props.mode === "single" && typeof props.modelValue === "string" ? props.modelValue : ""
);

const multipleIntegrationIds = ref<string[]>(
  props.mode === "multiple" && Array.isArray(props.modelValue) ? props.modelValue : []
);

const selectedOptions = reactive({
  ...props.userIntegrations.reduce(
    (acc, integration) => {
      acc[integration.id] = false;
      return acc;
    },
    {} as Record<string, boolean>
  ),
});

function onUpdate() {
  if (props.mode === "single") {
    emit(
      "update:modelValue",
      singleIntegrationId.value as Mode extends "single" ? string : string[]
    );
  } else {
    const selected = Object.entries(unref(selectedOptions))
      .filter(([_, selected]) => selected)
      .map(([id]) => id);
    emit("update:modelValue", selected as Mode extends "single" ? string : string[]);
  }
}

onBeforeMount(() => {
  if (props.userIntegrations.length === 1) {
    const id = props.userIntegrations[0].id;
    if (props.mode === "single" && !singleIntegrationId.value.length) {
      singleIntegrationId.value = id;
    } else if (props.mode === "multiple" && !multipleIntegrationIds.value.length) {
      multipleIntegrationIds.value = [id];
      selectedOptions[id] = true;
      onUpdate();
    }
  }
  if (
    props.mode === "multiple" &&
    Array.isArray(props.modelValue) &&
    props.modelValue.length
  ) {
    for (const id of props.modelValue) {
      if (selectedOptions[id] !== undefined) selectedOptions[id] = true;
    }
  }
});
</script>
